import {
  // loginEnd,
  loginFailure,
  loginStart,
  loginSuccess,
  logout,
  signUpFailure,
  signUpStart,
  // signUpStart,
  signUpSuccess,
  // resetError,
} from "./userRedux";
import { publicRequest } from "../api/requestMethods";
import { signUpAttributionFields } from "../utils/signUpAttribution";

/** Public GET — whether SMS OTP should be offered (kill switch, circuit, optional balance). */
export const fetchAuthOptions = async () => {
  const res = await publicRequest.get("/user/auth-options");
  return res.data;
};

export const login = async (dispatch, user) => {
  dispatch(loginStart());
  // dispatch(resetError());
  try {
    const res = await publicRequest.post("/user/signin", user);
    dispatch(loginSuccess(res.data));
  } catch (err) {
    console.log(err.message, "login failure!");
    const errorMessage =
      err.response && err.response.data && err.response.data.message
        ? { message: err.response.data.message }
        : { message: err.message };
    dispatch(loginFailure(errorMessage));
  }
};

export const signup = async (dispatch, user) => {
  dispatch(signUpStart());
  try {
    const res = await publicRequest.post("mobileUser/signup", { ...user, ...signUpAttributionFields() });
    dispatch(signUpSuccess(res.data));
    // return res;
  } catch (error) {
    const errorMessageSignUp = error.response.data.message;
    dispatch(signUpFailure(errorMessageSignUp));
    // throw error;
  }
};
// Unified Google Authentication (handles both login and signup automatically)
export const authenticateWithGoogle = async (dispatch, googleData) => {
  dispatch(loginStart()); // Use loginStart for loading state
  try {
    const res = await publicRequest.post("/user/googleSignin", {
      accessToken: googleData.accessToken,
      platform: "web",
      ...signUpAttributionFields(),
    });
    dispatch(loginSuccess(res.data));
    // The server's word on whether this call created the account.
    return { isNewUser: res.data?.isNewUser === true };
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    dispatch(loginFailure(errorMessage));
    throw error; // Allow caller to handle error
  }
};

// Legacy functions kept for backward compatibility (redirect to unified function)
export const signUpWithGoogle = async (dispatch, googleData) => {
  return authenticateWithGoogle(dispatch, googleData);
};

export const loginWithGoogle = async (dispatch, user) => {
  return authenticateWithGoogle(dispatch, user);
};

export const logoutUser = (dispatch) => {
  dispatch(logout());
};

// Phone Authentication with OTP
//
// `channel` is OPTIONAL and advisory: "sms" | "whatsapp" | "email" | "auto".
// The server decides what actually goes out and reports it back as
// `channel` + `maskedDestination` on the response — those two fields, not this
// argument, are what the UI is allowed to tell the customer. Omitting the
// argument keeps the pre-July-2026 behaviour (server picks).
export const sendOTP = async (phoneNumber, channel) => {
  try {
    const body = { phoneNumber, platform: "web" };
    if (channel) body.channel = channel;
    const res = await publicRequest.post("/user/send-otp", body);
    return res.data;
  } catch (error) {
    // ── Direct-login gate: verified user, no OTP needed ──────────────────
    if (error.response?.status === 409 && error.response?.data?.directLoginAvailable) {
      return { directLoginAvailable: true };
    }
    // ─────────────────────────────────────────────────────────────────────
    const payload = error.response?.data;
    const errorMessage = payload?.message || error.message;
    const err = new Error(errorMessage);
    if (payload?.code) err.code = payload.code;
    if (payload?.suggestEmail != null) err.suggestEmail = !!payload.suggestEmail;
    // The server now names a fallback that actually exists. Before July 2026 it
    // only said "sign in with email", which was impossible to follow: the sole
    // email sign-in was /user/signin, and phone-registered customers have never
    // set a password. `emailOtpAvailable` means "ask for an address and use the
    // passwordless email OTP".
    if (payload?.emailOtpAvailable != null) {
      err.emailOtpAvailable = !!payload.emailOtpAvailable;
    }
    // Which channel the failure is about, when the server names one. Lets the
    // UI say "WhatsApp isn't available right now" instead of a generic shrug.
    if (payload?.channel) err.channel = payload.channel;
    err.httpStatus = error.response?.status;
    throw err;
  }
};

// ── Passwordless email OTP ───────────────────────────────────────────────────
// Same 6-digit code, same 5-minute expiry, same OTP screen — just a different
// door. Needed because an SMS outage locks out precisely the customers who have
// no password to fall back on.
export const sendEmailOTP = async (email) => {
  try {
    const res = await publicRequest.post("/user/send-email-otp", {
      email,
      platform: "web",
    });
    return res.data;
  } catch (error) {
    const payload = error.response?.data;
    const err = new Error(payload?.message || error.message);
    if (payload?.code) err.code = payload.code;
    err.httpStatus = error.response?.status;
    throw err;
  }
};

export const verifyEmailOTP = async (dispatch, email, otp) => {
  dispatch(loginStart());
  try {
    const res = await publicRequest.post("/user/verify-email-otp", {
      email,
      otp,
      platform: "web",
      ...signUpAttributionFields(),
    });
    dispatch(loginSuccess(res.data));
    return res.data;
  } catch (error) {
    const payload = error.response?.data;
    const errorMessage = payload?.message || error.message;
    // Match verifyOTP exactly — the reducer expects a payload object.
    dispatch(loginFailure({ message: errorMessage }));
    const err = new Error(errorMessage);
    if (payload?.code) err.code = payload.code;
    err.httpStatus = error.response?.status;
    throw err;
  }
};

// Zero-Twilio login for returning verified users
// Called when sendOTP returns { directLoginAvailable: true }
export const phoneDirectLogin = async (dispatch, phoneNumber) => {
  dispatch(loginStart());
  try {
    const res = await publicRequest.post("/user/phone-signin", {
      phoneNumber,
      platform: "web",
    });
    dispatch(loginSuccess(res.data));
    return res.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    dispatch(loginFailure({ message: errorMessage }));
    throw new Error(errorMessage);
  }
};

export const verifyOTP = async (dispatch, phoneNumber, otp) => {
  dispatch(loginStart());
  try {
    const res = await publicRequest.post("/user/verify-otp", {
      phoneNumber,
      otp,
      platform: "web",
      // A friend's invite code or an Order together link, if this shopper
      // arrived by one. The server attributes them only when this
      // verification creates the account.
      ...signUpAttributionFields(),
    });
    dispatch(loginSuccess(res.data));
    return res.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    dispatch(loginFailure({ message: errorMessage }));
    throw new Error(errorMessage);
  }
};

// Phone Signup with OTP
export const signupWithPhone = async (dispatch, userData) => {
  dispatch(signUpStart());
  try {
    const res = await publicRequest.post("/user/phone-signup", {
      ...userData,
      platform: "web", // Add platform identifier
      ...signUpAttributionFields(),
    });
    dispatch(signUpSuccess(res.data));
    return res.data;
  } catch (error) {
    const errorMessageSignUp = error.response?.data?.message || error.message;
    dispatch(signUpFailure(errorMessageSignUp));
    throw new Error(errorMessageSignUp);
  }
};

// Complete profile for new phone-verified users
// Called after OTP verification when the user has no name (isNewUser: true)
export const completePhoneProfile = async (dispatch, { name, email, accessToken }) => {
  try {
    const res = await publicRequest.post(
      "/user/complete-profile",
      { name, email },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    // Update Redux store with completed profile
    dispatch(loginSuccess(res.data));
    return res.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    throw new Error(errorMessage);
  }
};
